import {
  CampaignCondition,
  CampaignConditionNewFormat,
  CampaignEffect,
  CampaignTriggers,
  CampaignTriggersUnion,
  CampaignTypeUnion,
  CampaignTypes,
  DirectCampaignRule,
  ReferralCampaignRule,
  SelectedSchemaCondition,
} from "types/campaigns";
import {
  CustomEventSchema,
  SchemaAttribute,
  SchemaAttributeType,
} from "types/customEvents";
import {
  findConditionIdPath,
  getTransactionAttribute,
  getTransactionParam,
  isNewFormat,
} from "../campaignHelpers";
import {
  achievementEventDirectCampaignConditions,
  achievementEventReferralCampaignConditions,
  directCustomerConditions,
  eventDirectCampaignConditions,
  eventReferralCampaignConditions,
  getInitialDataForSelectedSchemaConditions,
  referralCampaignCustomerConditions,
  transactionDirectCampaignConditions,
  transactionReferralCampaignConditions,
} from "./rules";

const isRuleUnitsSettingsOverridden = (effect: CampaignEffect) => {
  return (
    effect.unitsExpirationRule?.expirationStrategy === "expression" ||
    effect.unitsExpirationRule?.expirationStrategy === "never_expires" ||
    effect.unitsLockRule?.lockStrategy === "expression" ||
    effect.unitsLockRule?.lockStrategy === "no_pending"
  );
};

export const getDefaultType = ({
  attribute,
  campaignTrigger,
  selectedSchemaDetails,
  campaignType,
}: {
  attribute: string;
  campaignTrigger: CampaignTriggersUnion;
  campaignType: CampaignTypeUnion;
  selectedSchemaDetails?: CustomEventSchema;
}) => {
  const isReferralCampaign = campaignType === CampaignTypes.REFERRAL;

  switch (campaignTrigger) {
    case CampaignTriggers.TRANSACTION:
    case CampaignTriggers.RETURN_TRANSACTION:
      const conditions = isReferralCampaign
        ? transactionReferralCampaignConditions
        : transactionDirectCampaignConditions;

      return conditions.find(
        (condition) => findConditionIdPath(condition) === attribute,
      )?.type;
    case CampaignTriggers.INTERNAL_EVENT:
      const internalEventConditions = isReferralCampaign
        ? achievementEventReferralCampaignConditions
        : achievementEventDirectCampaignConditions;

      return (
        internalEventConditions.find(
          (condition) => findConditionIdPath(condition) === attribute,
        )?.type || undefined
      );
    case CampaignTriggers.CUSTOM_EVENT:
    case CampaignTriggers.CUSTOM_EVENT_UNIQUE_CODE:
      const eventCampaignAttribute = /event.body.(.*)/;

      if (attribute?.match(eventCampaignAttribute)) {
        const conditionsBasedOnSchema = generateSchemaConditions(
          selectedSchemaDetails?.schema?.fields as SchemaAttribute[],
        );
        return (
          conditionsBasedOnSchema.find(
            ({ attribute: fieldAttribute }) => fieldAttribute === attribute,
          )?.type || ""
        );
      } else {
        const externalEventConditions = isReferralCampaign
          ? eventReferralCampaignConditions
          : eventDirectCampaignConditions;

        return (
          externalEventConditions.find(
            (condition) => findConditionIdPath(condition) === attribute,
          )?.type || ""
        );
      }
    case CampaignTriggers.ACHIEVEMENT:
      const achivementConditions = isReferralCampaign
        ? referralCampaignCustomerConditions
        : directCustomerConditions;

      return (
        achivementConditions.find(
          (condition) => findConditionIdPath(condition) === attribute,
        )?.type || undefined
      );
    case CampaignTriggers.TIME:
      return transactionDirectCampaignConditions
        .filter(
          (item) => !findConditionIdPath(item)?.includes("transaction.items."),
        )
        .find((condition) => findConditionIdPath(condition) === attribute)
        ?.type;
    default:
      return "";
  }
};

// Function used to find label for new format conditions
const getConditionLabel = (
  field: string,
  campaignType: CampaignTypeUnion,
  campaignTrigger: CampaignTriggersUnion,
) => {
  const isReferralCampaign = campaignType === CampaignTypes.REFERRAL;

  switch (campaignTrigger) {
    case CampaignTriggers.TRANSACTION:
    case CampaignTriggers.RETURN_TRANSACTION:
      const conditions = isReferralCampaign
        ? transactionReferralCampaignConditions
        : transactionDirectCampaignConditions;

      return conditions.find(
        (condition) => findConditionIdPath(condition) === field,
      )?.label;
    case CampaignTriggers.INTERNAL_EVENT:
      const internalEventConditions = isReferralCampaign
        ? achievementEventReferralCampaignConditions
        : achievementEventDirectCampaignConditions;

      return internalEventConditions.find(
        (condition) => findConditionIdPath(condition) === field,
      )?.label;
    case CampaignTriggers.CUSTOM_EVENT:
    case CampaignTriggers.CUSTOM_EVENT_UNIQUE_CODE:
      const externalEventConditions = isReferralCampaign
        ? eventReferralCampaignConditions
        : eventDirectCampaignConditions;

      return externalEventConditions.find(
        (condition) => findConditionIdPath(condition) === field,
      )?.label;
    case CampaignTriggers.ACHIEVEMENT:
      const achivementConditions = isReferralCampaign
        ? referralCampaignCustomerConditions
        : directCustomerConditions;

      return achivementConditions.find(
        (condition) => findConditionIdPath(condition) === field,
      )?.label;
    case CampaignTriggers.TIME:
      return transactionDirectCampaignConditions
        .filter(
          (item) => !findConditionIdPath(item)?.includes("transaction.items."),
        )
        .find((condition) => findConditionIdPath(condition) === field)?.label;
    default:
      return "";
  }
};

export const generateRules = ({
  rules,
  campaignTrigger,
  campaignType,
  selectedSchemaDetails,
}: {
  rules: (DirectCampaignRule | ReferralCampaignRule)[];
  campaignTrigger: CampaignTriggersUnion;
  campaignType: CampaignTypeUnion;
  selectedSchemaDetails?: CustomEventSchema;
}) => {
  switch (campaignTrigger) {
    case CampaignTriggers.TRANSACTION:
    case CampaignTriggers.RETURN_TRANSACTION:
      return rules.map(
        ({ conditions, effects, name, description, ...rule }) => {
          return {
            conditions: conditions?.map((condition) => {
              const setUniqueField = (field: string) => {
                switch (field) {
                  case "transaction.items":
                    return `transaction.items.${(condition as CampaignConditionNewFormat).value.aggregation?.field}`;
                  case "customer.wallets":
                    return `customer.wallets.${(condition as CampaignConditionNewFormat).value.params?.field}`;
                  case "referrer.wallets":
                    return `referrer.wallets.${(condition as CampaignConditionNewFormat).value.params?.field}`;
                  default:
                    return (
                      (condition as CampaignConditionNewFormat)?.value?.field ||
                      ""
                    );
                }
              };
              const uniqueField = setUniqueField(
                (condition as CampaignConditionNewFormat)?.value?.field,
              );

              return isNewFormat(condition)
                ? {
                    value: {
                      aggregation: condition.value?.aggregation,
                      field: uniqueField,
                      filterCode: condition.value.filterCode,
                      params: condition.value.params,
                    },
                    condition: condition.condition,
                    label: getConditionLabel(
                      uniqueField,
                      campaignType,
                      campaignTrigger,
                    ),
                    type: getDefaultType({
                      attribute: uniqueField,
                      campaignType,
                      campaignTrigger,
                    }),
                    isEditing: false,
                    isSaved: true,
                  }
                : {
                    attribute:
                      condition.operator === "expression"
                        ? "expression"
                        : getTransactionAttribute(condition.attribute),
                    data:
                      getDefaultType({
                        attribute: getTransactionAttribute(condition.attribute),
                        campaignType,
                        campaignTrigger,
                      }) === "boolean"
                        ? !!condition.data
                        : condition.data,
                    label: condition.attribute,
                    operator: condition.operator,
                    param: getTransactionParam(condition.attribute),
                    type:
                      condition.operator === "expression"
                        ? "expression"
                        : getDefaultType({
                            attribute: getTransactionAttribute(
                              condition.attribute,
                            ),
                            campaignType,
                            campaignTrigger,
                          }),
                    isEditing: false,
                    isSaved: true,
                  };
            }),
            effects: effects?.map((effect) => {
              return {
                couponValueRule: effect.couponValueRule,
                customAttributeKey: effect.customAttributeKey,
                customAttributeValueRule: effect.customAttributeValueRule,
                effect: effect.effect,
                label: effect.effect,
                pointsRule: effect.pointsRule,
                unitsExpirationRule: effect.unitsExpirationRule,
                unitsLockRule: effect.unitsLockRule,
                rewardId: effect.rewardId,
                walletCode: effect.walletCode,
                isEditing: false,
                isSaved: true,
                isChecked: isRuleUnitsSettingsOverridden(effect),
              };
            }),
            ...("target" in rule && {
              target: rule.target,
            }),
            name,
            description,
          };
        },
      );
    case CampaignTriggers.CUSTOM_EVENT:
    case CampaignTriggers.CUSTOM_EVENT_UNIQUE_CODE:
      return rules.map(
        ({ conditions, effects, name, description, ...rule }) => {
          return {
            conditions: conditions?.map((condition) => {
              const setUniqueField = (field: string) => {
                switch (field) {
                  case "customer.wallets":
                    return `customer.wallets.${(condition as CampaignConditionNewFormat).value.params?.field}`;
                  case "referrer.wallets":
                    return `referrer.wallets.${(condition as CampaignConditionNewFormat).value.params?.field}`;
                  default:
                    return (
                      (condition as CampaignConditionNewFormat)?.value?.field ||
                      ""
                    );
                }
              };
              const uniqueField = setUniqueField(
                (condition as CampaignConditionNewFormat)?.value?.field,
              );

              return isNewFormat(condition)
                ? {
                    value: {
                      field: uniqueField,
                      params: condition.value.params,
                    },
                    condition: condition.condition,
                    label: getConditionLabel(
                      uniqueField,
                      campaignType,
                      campaignTrigger,
                    ),
                    type: getDefaultType({
                      attribute: uniqueField,
                      campaignType,
                      campaignTrigger,
                    }),
                    isEditing: false,
                    isSaved: true,
                  }
                : {
                    attribute:
                      condition.operator === "expression"
                        ? "expression"
                        : condition.attribute,
                    data:
                      getDefaultType({
                        attribute: condition.attribute,
                        campaignTrigger,
                        campaignType,
                        selectedSchemaDetails,
                      }) === "boolean"
                        ? !!condition.data
                        : condition.data,
                    label: condition.attribute,
                    operator: condition.operator,
                    param: condition.attribute,
                    type:
                      condition.operator === "expression"
                        ? "expression"
                        : getDefaultType({
                            attribute: condition.attribute,
                            campaignTrigger,
                            campaignType,
                            selectedSchemaDetails,
                          }),
                    isEditing: false,
                    isSaved: true,
                  };
            }),
            effects: effects?.map((effect) => {
              return {
                couponValueRule: effect.couponValueRule,
                customAttributeKey: effect.customAttributeKey,
                customAttributeValueRule: effect.customAttributeValueRule,
                effect: effect.effect,
                label: effect.effect,
                pointsRule: effect.pointsRule,
                unitsExpirationRule: effect.unitsExpirationRule,
                unitsLockRule: effect.unitsLockRule,
                rewardId: effect.rewardId,
                walletCode: effect.walletCode,
                isChecked: isRuleUnitsSettingsOverridden(effect),
              };
            }),
            ...("target" in rule && {
              target: rule.target,
            }),
            name,
            description,
          };
        },
      );
    case CampaignTriggers.INTERNAL_EVENT:
      return rules.map(
        ({ conditions, effects, name, description, ...rule }) => {
          return {
            conditions: conditions?.map((condition) => {
              const setUniqueField = (field: string) => {
                switch (field) {
                  case "customer.wallets":
                    return `customer.wallets.${(condition as CampaignConditionNewFormat).value.params?.field}`;
                  case "referrer.wallets":
                    return `referrer.wallets.${(condition as CampaignConditionNewFormat).value.params?.field}`;
                  default:
                    return (
                      (condition as CampaignConditionNewFormat)?.value?.field ||
                      ""
                    );
                }
              };
              const uniqueField = setUniqueField(
                (condition as CampaignConditionNewFormat)?.value?.field,
              );

              return isNewFormat(condition)
                ? {
                    value: {
                      field: uniqueField,
                      params: condition.value.params,
                    },
                    condition: condition.condition,
                    label: getConditionLabel(
                      uniqueField,
                      campaignType,
                      campaignTrigger,
                    ),
                    type: getDefaultType({
                      attribute: uniqueField,
                      campaignType,
                      campaignTrigger,
                    }),
                    isEditing: false,
                    isSaved: true,
                  }
                : {
                    attribute:
                      condition.operator === "expression"
                        ? "expression"
                        : condition.attribute,
                    data:
                      getDefaultType({
                        attribute: condition.attribute,
                        campaignTrigger,
                        campaignType,
                      }) === "boolean"
                        ? !!condition.data
                        : condition.data,
                    label: condition.attribute,
                    operator: condition.operator,
                    param: condition.attribute,
                    type:
                      condition.operator === "expression"
                        ? "expression"
                        : getDefaultType({
                            attribute: condition.attribute,
                            campaignTrigger,
                            campaignType,
                          }),
                    isEditing: false,
                    isSaved: true,
                  };
            }),
            effects: effects?.map((effect) => {
              return {
                couponValueRule: effect.couponValueRule,
                customAttributeKey: effect.customAttributeKey,
                customAttributeValueRule: effect.customAttributeValueRule,
                effect: effect.effect,
                label: effect.effect,
                pointsRule: effect.pointsRule,
                unitsExpirationRule: effect.unitsExpirationRule,
                unitsLockRule: effect.unitsLockRule,
                rewardId: effect.rewardId,
                walletCode: effect.walletCode,
                isChecked: isRuleUnitsSettingsOverridden(effect),
              };
            }),
            ...("target" in rule && {
              target: rule.target,
            }),
            name,
            description,
          };
        },
      );
    case CampaignTriggers.ACHIEVEMENT:
      return rules.map(
        ({ conditions, effects, name, description, ...rule }) => {
          return {
            conditions: conditions?.map((condition) => {
              const setUniqueField = (field: string) => {
                switch (field) {
                  case "customer.wallets":
                    return `customer.wallets.${(condition as CampaignConditionNewFormat).value.params?.field}`;
                  case "referrer.wallets":
                    return `referrer.wallets.${(condition as CampaignConditionNewFormat).value.params?.field}`;
                  default:
                    return (
                      (condition as CampaignConditionNewFormat)?.value?.field ||
                      ""
                    );
                }
              };
              const uniqueField = setUniqueField(
                (condition as CampaignConditionNewFormat)?.value?.field,
              );

              return isNewFormat(condition)
                ? {
                    value: {
                      field: uniqueField,
                      params: condition.value.params,
                    },
                    condition: condition.condition,
                    label: getConditionLabel(
                      uniqueField,
                      campaignType,
                      campaignTrigger,
                    ),
                    type: getDefaultType({
                      attribute: uniqueField,
                      campaignType,
                      campaignTrigger,
                    }),
                    isEditing: false,
                    isSaved: true,
                  }
                : {
                    attribute:
                      condition.operator === "expression"
                        ? "expression"
                        : getTransactionAttribute(condition.attribute),
                    data:
                      getDefaultType({
                        attribute: getTransactionAttribute(condition.attribute),
                        campaignTrigger,
                        campaignType,
                      }) === "boolean"
                        ? !!condition.data
                        : condition.data,
                    label: condition.attribute,
                    operator: condition.operator,
                    param: getTransactionParam(condition.attribute),
                    type:
                      condition.operator === "expression"
                        ? "expression"
                        : getDefaultType({
                            attribute: getTransactionAttribute(
                              condition.attribute,
                            ),
                            campaignTrigger,
                            campaignType,
                          }),
                    isEditing: false,
                    isSaved: true,
                  };
            }),
            effects: effects?.map((effect) => {
              return {
                couponValueRule: effect.couponValueRule,
                customAttributeKey: effect.customAttributeKey,
                customAttributeValueRule: effect.customAttributeValueRule,
                effect: effect.effect,
                label: effect.effect,
                pointsRule: effect.pointsRule,
                unitsExpirationRule: effect.unitsExpirationRule,
                unitsLockRule: effect.unitsLockRule,
                rewardId: effect.rewardId,
                walletCode: effect.walletCode,
                isChecked: isRuleUnitsSettingsOverridden(effect),
              };
            }),
            ...("target" in rule && {
              target: rule.target,
            }),
            name,
            description,
          };
        },
      );
    case CampaignTriggers.TIME:
      return rules.map(
        ({ conditions, effects, name, description, ...rule }) => {
          return {
            conditions: conditions?.map((condition) => {
              const setUniqueField = (field: string) => {
                switch (field) {
                  case "customer.wallets":
                    return `customer.wallets.${(condition as CampaignConditionNewFormat).value.params?.field}`;
                  default:
                    return (
                      (condition as CampaignConditionNewFormat)?.value?.field ||
                      ""
                    );
                }
              };
              const uniqueField = setUniqueField(
                (condition as CampaignConditionNewFormat)?.value?.field,
              );

              return isNewFormat(condition)
                ? {
                    value: {
                      field: uniqueField,
                      params: condition.value.params,
                    },
                    condition: condition.condition,
                    label: getConditionLabel(
                      uniqueField,
                      campaignType,
                      campaignTrigger,
                    ),
                    type: getDefaultType({
                      attribute: uniqueField,
                      campaignType,
                      campaignTrigger,
                    }),
                    isEditing: false,
                    isSaved: true,
                  }
                : {
                    attribute:
                      condition.operator === "expression"
                        ? "expression"
                        : condition.attribute,
                    data:
                      getDefaultType({
                        attribute: condition.attribute,
                        campaignTrigger,
                        campaignType,
                      }) === "boolean"
                        ? !!condition.data
                        : condition.data,
                    label: condition.attribute,
                    operator: condition.operator,
                    param: condition.attribute,
                    type:
                      condition.operator === "expression"
                        ? "expression"
                        : getDefaultType({
                            attribute: condition.attribute,
                            campaignTrigger,
                            campaignType,
                          }),
                    isEditing: false,
                    isSaved: true,
                  };
            }),
            effects: effects?.map((effect) => {
              return {
                couponValueRule: effect.couponValueRule,
                customAttributeKey: effect.customAttributeKey,
                customAttributeValueRule: effect.customAttributeValueRule,
                effect: effect.effect,
                label: effect.effect,
                pointsRule: effect.pointsRule,
                unitsExpirationRule: effect.unitsExpirationRule,
                unitsLockRule: effect.unitsLockRule,
                rewardId: effect.rewardId,
                walletCode: effect.walletCode,
                isChecked: isRuleUnitsSettingsOverridden(effect),
              };
            }),
            ...("target" in rule && {
              target: rule.target,
            }),
            name,
            description,
          };
        },
      );
    default:
      return rules;
  }
};

export const prepareSchemaConditionForAutocompleteVariables = (
  selectedSchemaConditions: SelectedSchemaCondition[],
  type?: string,
) => {
  return (
    selectedSchemaConditions &&
    selectedSchemaConditions
      .filter((item: SelectedSchemaCondition) =>
        type ? item.type === type : true,
      )
      ?.map((item: SelectedSchemaCondition) => ({
        label: item.label,
        code: item.attribute,
        codeRegexMatcher: new RegExp(`^${item.attribute}$`),
      }))
  );
};

export const generateSchemaExpressions = (
  schema: SchemaAttribute,
  name: string,
): any => {
  switch (schema.type) {
    case "number":
    case "boolean":
    case "text":
    case "datetime":
      return { name: name, type: schema.type };
    case "object":
      return schema.fields?.map((field: SchemaAttribute) =>
        generateSchemaExpressions(field, `${name}.${field.name}`),
      );
    case "collection":
      return schema.itemType?.fields?.map(
        (field: SchemaAttribute, index: number) =>
          generateSchemaExpressions(field, `${name}[${index}].${field.name}`),
      );
  }
};

export const generateSchemaConditions = (fields?: SchemaAttribute[]) => {
  if (!fields) return [];
  const conditions: SelectedSchemaCondition[] = [];

  const generateCondtionsForAttributes = (
    attributes:
      | { name: string; type: string }
      | { name: string; type: string }[],
    field: SchemaAttribute,
  ): void => {
    if (!Array.isArray(attributes)) {
      conditions.push({
        attribute: `event.body.${attributes?.name}`,
        data: getInitialDataForSelectedSchemaConditions(field.type || ""),
        label: `${field.description || field.name} (${attributes?.name})`,
        type: attributes.type as SchemaAttributeType,
        operator: field.type === "boolean" ? "is_equal" : undefined,
      });
    } else {
      return attributes.forEach((attr) =>
        generateCondtionsForAttributes(attr, field),
      );
    }
  };

  fields.forEach((schemaField) => {
    const attributes = generateSchemaExpressions(schemaField, schemaField.name);
    if (attributes.type) {
      return conditions.push({
        attribute: `event.body.${attributes.name}`,
        data: getInitialDataForSelectedSchemaConditions(schemaField.type || ""),
        label: `${schemaField.description || schemaField.name} (${attributes.name})`,
        type: schemaField.type as SchemaAttributeType,
        operator: schemaField.type === "boolean" ? "is_equal" : undefined,
      });
    }

    generateCondtionsForAttributes(attributes, schemaField);
  });
  return conditions;
};

export const filterOldTierConditions = ({
  conditions,
  rules,
  isEditMode,
}: {
  conditions: (
    | CampaignCondition
    | SelectedSchemaCondition
    | CampaignConditionNewFormat
  )[];
  rules: DirectCampaignRule[] | ReferralCampaignRule[];
  isEditMode: boolean;
}) => {
  // Backward compatibility for already added conditions.
  // Currently customer.levelId and referrer.levelId is depricated.
  const depricatedConditions = ["customer.levelId", "referrer.levelId"];

  isEditMode &&
    rules.forEach((rule) =>
      rule.conditions?.forEach((rule) => {
        const isConditionAlreadyAdded = depricatedConditions.includes(
          findConditionIdPath(rule),
        );
        if (isConditionAlreadyAdded) {
          const conditionIndex = depricatedConditions.findIndex(
            (attribute) => attribute === findConditionIdPath(rule),
          );

          depricatedConditions.splice(conditionIndex, 1);
        }
      }),
    );

  const filtered = conditions.filter(
    (condition) =>
      !depricatedConditions.includes(findConditionIdPath(condition)),
  );
  return filtered;
};
